import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaEnvelope,
  FaPaperPlane,
  FaArrowLeft,
  FaShieldAlt,
  FaCheckCircle,
  FaInbox,
  FaMailBulk,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import toast from "react-hot-toast";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        "/api/password-reset/forgot-password",
        { email },
      );
      setSubmitted(true);
      toast.success("Password reset email sent! Check your inbox.");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to send reset email",
      );
    } finally {
      setLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const successVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 400, damping: 20 },
    },
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        background: isDarkMode ? "#0f0f1a" : "#f8fafc",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Particles */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 10,
            }}
            style={{
              position: "absolute",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              background: `linear-gradient(135deg, #8b5cf6, #3b82f6)`,
              borderRadius: "50%",
              filter: "blur(2px)",
            }}
          />
        ))}
      </div>

      {/* Floating gradient orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.1, 0.3],
        }}
        transition={{ duration: 8, repeat: Infinity }}
        style={{
          position: "absolute",
          top: "10%",
          right: "5%",
          width: 300,
          height: 300,
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.1, 0.2],
        }}
        transition={{ duration: 10, repeat: Infinity, delay: 2 }}
        style={{
          position: "absolute",
          bottom: "10%",
          left: "5%",
          width: 250,
          height: 250,
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          maxWidth: "460px",
          width: "100%",
          background: isDarkMode
            ? "rgba(30, 30, 45, 0.9)"
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: "32px",
          padding: "2.5rem",
          boxShadow: isDarkMode
            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
          border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Animated gradient border top */}
        <motion.div
          animate={{
            background: [
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
              "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            borderRadius: "32px 32px 0 0",
          }}
        />

        <AnimatePresence mode="wait">
          {!submitted ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                variants={itemVariants}
                style={{ textAlign: "center", marginBottom: "2rem" }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      width: "60px",
                      height: "60px",
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaMailBulk size={28} color="white" />
                  </div>
                </motion.div>
                <h1
                  style={{
                    fontSize: "2rem",
                    marginBottom: "0.5rem",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontWeight: 700,
                  }}
                >
                  Forgot Password?
                </h1>
                <p style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                  Enter your email to reset your password
                </p>
              </motion.div>

              <form onSubmit={handleSubmit}>
                <motion.div
                  variants={itemVariants}
                  style={{ marginBottom: "1.5rem" }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      color: isDarkMode ? "#cbd5e1" : "#475569",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                    }}
                  >
                    Email Address
                  </label>
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        position: "absolute",
                        left: "1rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color:
                          emailFocused || email
                            ? "#8b5cf6"
                            : isDarkMode
                              ? "#6b7280"
                              : "#9ca3af",
                        transition: "color 0.3s",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    >
                      <FaEnvelope size={18} />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setEmailFocused(true)}
                      onBlur={() => setEmailFocused(false)}
                      required
                      placeholder="Enter your registered email"
                      style={{
                        width: "100%",
                        padding: "0.9rem 1rem 0.9rem 3rem",
                        border: `2px solid ${
                          emailFocused
                            ? "#8b5cf6"
                            : isDarkMode
                              ? "rgba(139, 92, 246, 0.2)"
                              : "rgba(139, 92, 246, 0.1)"
                        }`,
                        borderRadius: "16px",
                        outline: "none",
                        fontSize: "1rem",
                        transition: "all 0.3s",
                        backgroundColor: isDarkMode
                          ? "rgba(0,0,0,0.3)"
                          : "white",
                        color: isDarkMode ? "#f1f5f9" : "#1e293b",
                      }}
                    />
                  </div>
                  <p
                    style={{
                      fontSize: "0.7rem",
                      color: isDarkMode ? "#94a3b8" : "#64748b",
                      marginTop: "0.5rem",
                    }}
                  >
                    We'll send a password reset link to this email
                  </p>
                </motion.div>

                <motion.button
                  variants={itemVariants}
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  style={{
                    width: "100%",
                    padding: "1rem",
                    background: loading
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "16px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    transition: "all 0.3s",
                    boxShadow: loading
                      ? "none"
                      : "0 4px 14px 0 rgba(139,92,246,0.4)",
                  }}
                >
                  <FaPaperPlane /> {loading ? "Sending..." : "Send Reset Link"}
                </motion.button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              variants={successVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{ textAlign: "center" }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 20,
                  delay: 0.2,
                }}
                style={{
                  width: "80px",
                  height: "80px",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1.5rem",
                  color: "white",
                  fontSize: "2.5rem",
                  boxShadow: "0 10px 25px -5px rgba(16,185,129,0.4)",
                }}
              >
                <FaCheckCircle />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  marginBottom: "1rem",
                  fontWeight: 700,
                }}
              >
                Check Your Inbox!
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                style={{
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  marginBottom: "1rem",
                }}
              >
                We've sent a password reset link to:
              </motion.p>
              <motion.p
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                style={{
                  background: isDarkMode
                    ? "rgba(139,92,246,0.2)"
                    : "rgba(139,92,246,0.1)",
                  color: "#8b5cf6",
                  fontWeight: 600,
                  marginBottom: "2rem",
                  padding: "0.5rem 1rem",
                  borderRadius: "12px",
                  display: "inline-block",
                  wordBreak: "break-all",
                }}
              >
                {email}
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  marginBottom: "1rem",
                }}
              >
                <FaInbox size={16} color="#8b5cf6" />
                <p
                  style={{
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    fontSize: "0.85rem",
                  }}
                >
                  Didn't receive the email? Check your spam folder
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          variants={itemVariants}
          style={{ textAlign: "center", marginTop: "1.5rem" }}
        >
          <Link
            to="/login"
            style={{
              color: isDarkMode ? "#94a3b8" : "#64748b",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: "0.875rem",
              transition: "color 0.3s",
            }}
            onMouseEnter={(e) => (e.target.style.color = "#8b5cf6")}
            onMouseLeave={(e) =>
              (e.target.style.color = isDarkMode ? "#94a3b8" : "#64748b")
            }
          >
            <FaArrowLeft size={12} /> Back to Login
          </Link>
        </motion.div>

        {/* Security Badge */}
        <motion.div
          variants={itemVariants}
          style={{
            marginTop: "1.5rem",
            paddingTop: "1rem",
            borderTop: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span
            style={{
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontSize: "0.7rem",
            }}
          >
            Secure password reset with 256-bit encryption
          </span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;

